import React from "react";
import { useSearchParams } from "react-router-dom";
import api, { MEDIA } from "../../lib/api";
import { toast } from "sonner";
import {
    Plus, Edit2, Trash2, X, Search, ChevronRight, Folder, FolderOpen,
    Home, Package, Tag, Percent, Star, Upload, GripVertical,
    PlayCircle, BookOpen, FileText, Download, Eye, ChevronDown
} from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";

const slugify = (str) =>
    str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
       .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").trim();

function buildPath(catId, allCats) {
    if (!catId) return [];
    const path = []; let current = catId; let i = 0;
    while (current && i++ < 10) {
        const cat = allCats.find(c => c.category_id === current);
        if (!cat) break;
        path.unshift(cat);
        current = cat.parent_id || null;
    }
    return path;
}

// ─────────────────────────────────────────────────────────────────
export default function AdminProducts() {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentCatId = searchParams.get("cat") || null;

    const [allCats, setAllCats]         = React.useState([]);
    const [allProducts, setAllProducts] = React.useState([]);
    const [catEditing, setCatEditing]   = React.useState(null);
    const [prodEditing, setProdEditing] = React.useState(null);
    const [prodOpen, setProdOpen]       = React.useState(false);
    const [search, setSearch]           = React.useState("");
    const [showSugg, setShowSugg]       = React.useState(false);
    const [bulkSel, setBulkSel]         = React.useState([]);
    const [bulkOpen, setBulkOpen]       = React.useState(false);
    const [docsView, setDocsView]       = React.useState(null);
    const [dragging, setDragging]       = React.useState(null);
    const [dragOver, setDragOver]       = React.useState(null);
    const searchRef = React.useRef(null);

    const load = React.useCallback(async () => {
        const [cr, pr] = await Promise.all([
            api.get("/admin/categories"),
            api.get("/admin/products"),
        ]);
        setAllCats(cr.data || []);
        setAllProducts(pr.data || []);
    }, []);
    React.useEffect(() => { load(); }, [load]);

    // ── Derived ──────────────────────────────────────────────────
    const currentCat  = allCats.find(c => c.category_id === currentCatId) || null;
    const breadcrumb  = buildPath(currentCatId, allCats);
    const subCats     = allCats.filter(c => (c.parent_id || null) === currentCatId)
                               .sort((a, b) => (a.order || 0) - (b.order || 0));

    const subCountOf  = (id) => allCats.filter(c => (c.parent_id || null) === id).length;
    const prodCountOf = (id) => allProducts.filter(p => p.category_id === id).length;

    const q = search.trim().toLowerCase();
    const catProducts = React.useMemo(() => {
        let ps = currentCatId ? allProducts.filter(p => p.category_id === currentCatId) : allProducts;
        if (q) ps = ps.filter(p => p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q));
        return ps;
    }, [allProducts, currentCatId, q]);

    const suggestions = q.length >= 2 ? allProducts.filter(p =>
        p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q)
    ).slice(0, 10) : [];

    // ── Navigation ───────────────────────────────────────────────
    const navigate = (catId) => {
        setSearch(""); setBulkSel([]);
        catId ? setSearchParams({ cat: catId }) : setSearchParams({});
    };

    // ── Category CRUD ────────────────────────────────────────────
    const openNewCat = (parentId) =>
        setCatEditing({ name: "", slug: "", description: "", image: "", order: subCats.length, active: true, specs_schema: [], parent_id: parentId || null });

    const saveCat = async () => {
        if (!catEditing.name) return toast.error("Numele este obligatoriu");
        try {
            if (catEditing.category_id) await api.put(`/admin/categories/${catEditing.category_id}`, catEditing);
            else await api.post("/admin/categories", catEditing);
            toast.success("Salvat"); setCatEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };
    const delCat = async (cat) => {
        if (!window.confirm(`Ștergi categoria "${cat.name}"? Produsele rămân neatinse.`)) return;
        await api.delete(`/admin/categories/${cat.category_id}`);
        toast.success("Șters"); load();
    };

    // ── Category drag-drop reorder ────────────────────────────────
    const handleCatDrop = async (targetId) => {
        if (!dragging || dragging === targetId) { setDragging(null); setDragOver(null); return; }
        const siblings = allCats.filter(c => c.parent_id === currentCatId)
                                .sort((a, b) => (a.order || 0) - (b.order || 0));
        const fromIdx = siblings.findIndex(c => c.category_id === dragging);
        const toIdx   = siblings.findIndex(c => c.category_id === targetId);
        if (fromIdx < 0 || toIdx < 0) return;
        const reordered = [...siblings];
        const [moved] = reordered.splice(fromIdx, 1);
        reordered.splice(toIdx, 0, moved);
        setAllCats(prev => {
            const others = prev.filter(c => c.parent_id !== currentCatId);
            return [...others, ...reordered.map((c, i) => ({ ...c, order: i }))];
        });
        for (let i = 0; i < reordered.length; i++) {
            try { await api.put(`/admin/categories/${reordered[i].category_id}`, { ...reordered[i], order: i }); } catch {}
        }
        setDragging(null); setDragOver(null);
    };

    // ── Product CRUD ─────────────────────────────────────────────
    const openNewProd = () => {
        setProdEditing({
            code: "", name: "", slug: "", category_id: currentCatId || (allCats[0]?.category_id || ""),
            brand: "", price: 0, sale_price: null, stock: 0, images: [], thumbnail_index: 0,
            specs: {}, short_description: "", description: "", active: true, featured: false,
            share_enabled: true, google_merchant_enabled: true, is_clearance: false,
            install_video_url: "", install_manual_url: "", install_public: false, docs: []
        });
        setProdOpen(true);
    };
    const openEditProd = (p) => { setProdEditing(JSON.parse(JSON.stringify(p))); setProdOpen(true); };
    const saveProd = async () => {
        try {
            if (prodEditing.product_id) await api.put(`/admin/products/${prodEditing.product_id}`, prodEditing);
            else await api.post("/admin/products", prodEditing);
            toast.success("Salvat"); setProdOpen(false); load();
        } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
    };
    const delProd = async (id) => {
        if (!window.confirm("Sigur ștergi?")) return;
        await api.delete(`/admin/products/${id}`); toast.success("Șters"); load();
    };

    // ── Bulk ──────────────────────────────────────────────────────
    const toggleBulk = (id) => setBulkSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    const applyPromoBulk = async (pct) => {
        const p = parseFloat(pct);
        if (isNaN(p) || p <= 0 || p >= 100) { toast.error("Procent invalid"); return; }
        for (const id of bulkSel) {
            const prod = allProducts.find(x => x.product_id === id);
            if (prod) await api.put(`/admin/products/${id}`, { sale_price: Math.round(prod.price * (1 - p / 100) * 100) / 100, featured: true });
        }
        toast.success(`Aplicat -${p}% la ${bulkSel.length} produse`);
        setBulkOpen(false); setBulkSel([]); load();
    };
    const clearPromoBulk = async () => {
        for (const id of bulkSel) await api.put(`/admin/products/${id}`, { sale_price: null });
        toast.success("Promoții eliminate"); setBulkSel([]); load();
    };

    const showSearch = q.length >= 1;
    const showCatGrid = subCats.length > 0 || (!currentCatId && allCats.length === 0);
    const showProducts = !!currentCatId || showSearch;

    return (
        <div data-testid="admin-products" onClick={() => setShowSugg(false)}>

            {/* ── Top bar ── */}
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <nav className="flex items-center gap-1 flex-wrap mb-1">
                        <button onClick={() => navigate(null)}
                            className={`flex items-center gap-1 text-sm font-semibold hover:text-primary transition-colors ${!currentCatId ? "text-primary" : "text-muted-foreground"}`}>
                            <Home className="h-4 w-4" />
                            <span>Produse</span>
                        </button>
                        {breadcrumb.map(cat => (
                            <React.Fragment key={cat.category_id}>
                                <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                <button onClick={() => navigate(cat.category_id)}
                                    className={`text-sm font-semibold hover:text-primary transition-colors ${cat.category_id === currentCatId ? "text-foreground" : "text-muted-foreground"}`}>
                                    {cat.name}
                                </button>
                            </React.Fragment>
                        ))}
                    </nav>
                    {currentCat && <p className="text-xs text-muted-foreground">{subCats.length} subcategorii · {prodCountOf(currentCatId)} produse</p>}
                    {!currentCat && <p className="text-xs text-muted-foreground">{allCats.filter(c => !c.parent_id).length} categorii · {allProducts.length} produse total</p>}
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                    {bulkSel.length > 0 && (
                        <>
                            <button onClick={() => setBulkOpen(true)} className="btn-outline flex items-center gap-1.5 text-sm"><Percent className="h-4 w-4" /> Reducere ({bulkSel.length})</button>
                            <button onClick={clearPromoBulk} className="btn-outline text-sm">Șterge promo</button>
                        </>
                    )}
                    <button onClick={() => openNewCat(currentCatId)}
                        className="btn-outline flex items-center gap-1.5 text-sm">
                        <Folder className="h-4 w-4" />
                        {currentCatId ? "Subcategorie nouă" : "Categorie nouă"}
                    </button>
                    <button onClick={openNewProd} className="btn-primary flex items-center gap-1.5 text-sm">
                        <Plus className="h-4 w-4" /> Produs nou
                    </button>
                </div>
            </div>

            {/* ── Search ── */}
            <div className="relative mb-5" ref={searchRef} onClick={e => e.stopPropagation()}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <input value={search}
                    onChange={e => { setSearch(e.target.value); setShowSugg(true); }}
                    onFocus={() => search.trim().length >= 2 && setShowSugg(true)}
                    placeholder="Caută produse după cod sau nume..."
                    className="input-soft pl-9 w-full" autoComplete="off" />
                {search && (
                    <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                )}
                {showSugg && suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-xl shadow-lg max-h-80 overflow-y-auto">
                        {suggestions.map(p => {
                            const cat = allCats.find(c => c.category_id === p.category_id);
                            const thumb = p.images?.[p.thumbnail_index ?? 0] || p.images?.[0];
                            return (
                                <button key={p.product_id} type="button"
                                    onMouseDown={() => { setShowSugg(false); openEditProd(p); }}
                                    className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-muted border-b border-border last:border-0">
                                    <div className="w-10 h-10 rounded-lg bg-muted flex-shrink-0 overflow-hidden">
                                        {thumb ? <img src={MEDIA(thumb)} alt="" className="w-full h-full object-cover" /> : <Tag className="w-4 h-4 m-auto mt-3 text-muted-foreground" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{p.name}</div>
                                        <div className="text-xs text-muted-foreground"><span className="font-mono">{p.code}</span>{cat && ` · ${cat.name}`}</div>
                                    </div>
                                    <div className="text-xs text-right shrink-0">{(p.sale_price || p.price || 0).toFixed(2)} RON</div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ── Category grid ── */}
            {!showSearch && subCats.length > 0 && (
                <div className="mb-6">
                    <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">
                        {currentCatId ? "Subcategorii" : "Categorii"}
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                        {subCats.map(cat => (
                            <div key={cat.category_id}
                                draggable
                                onDragStart={e => { e.stopPropagation(); setDragging(cat.category_id); }}
                                onDragOver={e => { e.preventDefault(); setDragOver(cat.category_id); }}
                                onDrop={e => { e.preventDefault(); handleCatDrop(cat.category_id); }}
                                onDragEnd={() => { setDragging(null); setDragOver(null); }}
                                onClick={() => navigate(cat.category_id)}
                                className={`group relative bg-card border rounded-2xl overflow-hidden cursor-pointer transition-all duration-150
                                    ${dragging === cat.category_id ? "opacity-40 scale-95" : ""}
                                    ${dragOver === cat.category_id && dragging !== cat.category_id ? "border-primary ring-2 ring-primary/20 scale-[1.02]" : "border-border/60 hover:border-primary/40 hover:shadow-md"}`}>
                                <div className="aspect-[4/3] bg-muted overflow-hidden">
                                    {cat.image
                                        ? <img src={cat.image.startsWith("http") ? cat.image : MEDIA(cat.image)} alt={cat.name}
                                               className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                        : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/30">
                                            {subCountOf(cat.category_id) > 0
                                                ? <FolderOpen className="h-9 w-9 text-muted-foreground/30" />
                                                : <Folder className="h-9 w-9 text-muted-foreground/30" />}
                                          </div>}
                                </div>
                                <div className="p-2.5 pb-3">
                                    <div className="font-semibold text-sm truncate leading-tight">{cat.name}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                        {subCountOf(cat.category_id) > 0 && <><Folder className="h-2.5 w-2.5" />{subCountOf(cat.category_id)}<span className="mx-1">·</span></>}
                                        <Package className="h-2.5 w-2.5" />{prodCountOf(cat.category_id)}
                                    </div>
                                </div>
                                {/* Hover actions */}
                                <div className="absolute top-1.5 right-1.5 hidden group-hover:flex gap-0.5 bg-card/85 backdrop-blur-sm rounded-lg p-0.5 shadow-sm">
                                    <button onClick={e => { e.stopPropagation(); openNewCat(cat.category_id); }}
                                        className="p-1.5 hover:bg-muted rounded-md transition-colors" title="Subcategorie nouă">
                                        <Plus className="h-3 w-3" />
                                    </button>
                                    <button onClick={e => { e.stopPropagation(); setCatEditing(JSON.parse(JSON.stringify(cat))); }}
                                        className="p-1.5 hover:bg-muted rounded-md transition-colors" title="Editează">
                                        <Edit2 className="h-3 w-3" />
                                    </button>
                                    <button onClick={e => { e.stopPropagation(); delCat(cat); }}
                                        className="p-1.5 hover:bg-destructive/10 rounded-md text-destructive transition-colors" title="Șterge">
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                                {/* Drag handle */}
                                <div className="absolute top-1.5 left-1.5 hidden group-hover:flex p-1 bg-card/70 rounded-md cursor-grab text-muted-foreground">
                                    <GripVertical className="h-3 w-3" />
                                </div>
                                {!cat.active && (
                                    <div className="absolute bottom-8 left-2 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">Inactiv</div>
                                )}
                            </div>
                        ))}
                        {/* Add new category card */}
                        <button onClick={() => openNewCat(currentCatId)}
                            className="border-2 border-dashed border-border hover:border-primary/50 rounded-2xl flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary transition-all min-h-[120px] cursor-pointer">
                            <Plus className="h-7 w-7" />
                            <span className="text-xs font-medium">{currentCatId ? "Subcategorie" : "Categorie"}</span>
                        </button>
                    </div>
                </div>
            )}

            {/* ── Empty root state ── */}
            {!currentCatId && !showSearch && subCats.length === 0 && allCats.length === 0 && (
                <div className="py-24 text-center text-muted-foreground">
                    <Folder className="h-14 w-14 mx-auto mb-4 opacity-20" />
                    <p className="text-lg font-medium mb-2">Nicio categorie încă</p>
                    <p className="text-sm mb-6">Creează prima categorie pentru a organiza produsele</p>
                    <button onClick={() => openNewCat(null)} className="btn-primary">
                        <Plus className="h-4 w-4 mr-2" /> Adaugă prima categorie
                    </button>
                </div>
            )}

            {/* ── Products table ── */}
            {showProducts && (
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                            {showSearch ? `Rezultate căutare (${catProducts.length})` : `Produse (${catProducts.length})`}
                        </h2>
                    </div>
                    <div className="card-soft overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted">
                                <tr>
                                    <th className="px-3 py-2.5 w-8">
                                        <input type="checkbox"
                                            checked={bulkSel.length === catProducts.length && catProducts.length > 0}
                                            onChange={e => setBulkSel(e.target.checked ? catProducts.map(p => p.product_id) : [])} />
                                    </th>
                                    {["", "Cod", "Produs", "Preț", "Stoc", ""].map((h, i) =>
                                        <th key={i} className="text-left px-3 py-2.5 text-xs uppercase tracking-wide text-muted-foreground">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {catProducts.length === 0 && (
                                    <tr><td colSpan={7} className="px-3 py-12 text-center text-muted-foreground">
                                        {showSearch ? "Niciun produs găsit." : "Niciun produs în această categorie."}
                                    </td></tr>
                                )}
                                {catProducts.map(p => {
                                    const cat = allCats.find(c => c.category_id === p.category_id);
                                    const thumb = p.images?.[p.thumbnail_index ?? 0] || p.images?.[0];
                                    return (
                                        <tr key={p.product_id} className="border-t border-border hover:bg-muted/30 transition-colors">
                                            <td className="px-3 py-2"><input type="checkbox" checked={bulkSel.includes(p.product_id)} onChange={() => toggleBulk(p.product_id)} /></td>
                                            <td className="px-3 py-2">
                                                <div className="w-11 h-11 rounded-xl bg-muted overflow-hidden border border-border/50">
                                                    {thumb ? <img src={MEDIA(thumb)} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 m-3.5 text-muted-foreground" />}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{p.code || "—"}</td>
                                            <td className="px-3 py-2 max-w-xs">
                                                <div className="font-medium leading-tight">{p.name}</div>
                                                {(!currentCatId || showSearch) && cat && <div className="text-xs text-muted-foreground mt-0.5">{cat.name}</div>}
                                                <div className="flex gap-1 mt-1 flex-wrap">
                                                    {p.active ? <span className="chip-success text-[10px]">Activ</span> : <span className="chip bg-muted text-muted-foreground text-[10px]">Inactiv</span>}
                                                    {p.featured && <span className="chip-primary text-[10px]">Popular</span>}
                                                    {p.is_clearance && <span className="chip text-[10px] bg-orange-100 text-orange-800">Lichidare</span>}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                {p.sale_price
                                                    ? <><div className="font-semibold text-primary">{p.sale_price.toFixed(2)} RON</div><div className="text-xs text-muted-foreground line-through">{p.price?.toFixed(2)}</div></>
                                                    : <div className="font-medium">{p.price?.toFixed(2)} RON</div>}
                                            </td>
                                            <td className="px-3 py-2 text-center font-medium">{p.stock ?? "—"}</td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                {(p.install_video_url || p.install_manual_url || p.doc_ce_url || (p.staff_docs?.length > 0)) && (
                                                    <button onClick={() => setDocsView(p)} className="btn-ghost text-primary" title="Documente"><Eye className="h-4 w-4" /></button>
                                                )}
                                                <button onClick={() => openEditProd(p)} className="btn-ghost"><Edit2 className="h-4 w-4" /></button>
                                                <button onClick={() => delProd(p.product_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Category edit modal ── */}
            {catEditing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-border">
                            <h2 className="font-heading text-xl">
                                {catEditing.category_id ? "Editează categorie" : catEditing.parent_id ? "Subcategorie nouă" : "Categorie nouă"}
                            </h2>
                            <button onClick={() => setCatEditing(null)}><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input placeholder="Nume *" value={catEditing.name || ""}
                                    onChange={e => {
                                        const name = e.target.value;
                                        const wasAuto = !catEditing.slug || catEditing.slug === slugify(catEditing.name || "");
                                        setCatEditing(v => ({ ...v, name, slug: wasAuto ? slugify(name) : v.slug }));
                                    }} className="input-soft" />
                                <input placeholder="Slug *" value={catEditing.slug || ""}
                                    onChange={e => setCatEditing(v => ({ ...v, slug: e.target.value }))} className="input-soft" />
                                <div className="md:col-span-2">
                                    <label className="text-xs text-muted-foreground block mb-1">Categorie părinte</label>
                                    <select value={catEditing.parent_id || ""}
                                        onChange={e => setCatEditing(v => ({ ...v, parent_id: e.target.value || null }))} className="input-soft">
                                        <option value="">— Categorie principală —</option>
                                        {allCats.filter(c => c.category_id !== catEditing.category_id).map(c => (
                                            <option key={c.category_id} value={c.category_id}>{c.parent_id ? "↳ " : ""}{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <textarea placeholder="Descriere" value={catEditing.description || ""}
                                    onChange={e => setCatEditing(v => ({ ...v, description: e.target.value }))}
                                    rows={2} className="input-soft md:col-span-2" />
                                <div className="md:col-span-2">
                                    <label className="text-xs text-muted-foreground block mb-1">Imagine</label>
                                    <div className="flex gap-2 items-center">
                                        {catEditing.image && <img src={catEditing.image.startsWith("http") ? catEditing.image : MEDIA(catEditing.image)} alt="" className="w-16 h-16 object-cover rounded-lg border border-border" />}
                                        <input value={catEditing.image || ""}
                                            onChange={e => setCatEditing(v => ({ ...v, image: e.target.value }))}
                                            placeholder="URL imagine" className="input-soft flex-1" />
                                        <label className="btn-outline cursor-pointer text-sm flex items-center gap-1 shrink-0">
                                            <Upload className="h-4 w-4" /> Încarcă
                                            <input type="file" accept="image/*" className="hidden" onChange={async e => {
                                                if (!e.target.files[0]) return;
                                                const fd = new FormData(); fd.append("file", e.target.files[0]);
                                                try {
                                                    const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                                    setCatEditing(v => ({ ...v, image: res.data.url }));
                                                    toast.success("Imagine încărcată");
                                                } catch { toast.error("Eroare upload"); }
                                            }} />
                                        </label>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <label className="text-xs text-muted-foreground">Ordine</label>
                                    <input type="number" value={catEditing.order ?? 0}
                                        onChange={e => setCatEditing(v => ({ ...v, order: Number(e.target.value) }))}
                                        className="input-soft w-24" />
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="checkbox" checked={catEditing.active !== false}
                                            onChange={e => setCatEditing(v => ({ ...v, active: e.target.checked }))} />
                                        Activ
                                    </label>
                                </div>
                            </div>

                            {/* Specs schema */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="font-semibold text-sm">Specificații produs</h3>
                                    <button type="button" onClick={() => setCatEditing(v => ({ ...v, specs_schema: [...(v.specs_schema || []), { key: "", label: "", type: "text", options: [], unit: "" }] }))}
                                        className="btn-outline !py-1.5 text-xs flex items-center gap-1">
                                        <Plus className="h-3 w-3" /> Adaugă câmp
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {(catEditing.specs_schema || []).map((s, i) => (
                                        <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                                            <input placeholder="cheie" value={s.key}
                                                onChange={e => { const ns = [...catEditing.specs_schema]; ns[i] = { ...ns[i], key: e.target.value }; setCatEditing(v => ({ ...v, specs_schema: ns })); }}
                                                className="input-soft !py-1.5 text-xs col-span-3" />
                                            <input placeholder="etichetă" value={s.label}
                                                onChange={e => { const ns = [...catEditing.specs_schema]; ns[i] = { ...ns[i], label: e.target.value }; setCatEditing(v => ({ ...v, specs_schema: ns })); }}
                                                className="input-soft !py-1.5 text-xs col-span-3" />
                                            <select value={s.type}
                                                onChange={e => { const ns = [...catEditing.specs_schema]; ns[i] = { ...ns[i], type: e.target.value }; setCatEditing(v => ({ ...v, specs_schema: ns })); }}
                                                className="input-soft !py-1.5 text-xs col-span-2">
                                                <option value="text">Text</option>
                                                <option value="number">Număr</option>
                                                <option value="select">Select</option>
                                                <option value="boolean">Da/Nu</option>
                                            </select>
                                            <input placeholder="opțiuni (sep ,)" value={(s.options || []).join(", ")}
                                                onChange={e => { const ns = [...catEditing.specs_schema]; ns[i] = { ...ns[i], options: e.target.value.split(",").map(x => x.trim()).filter(Boolean) }; setCatEditing(v => ({ ...v, specs_schema: ns })); }}
                                                disabled={s.type !== "select"} className="input-soft !py-1.5 text-xs col-span-2 disabled:opacity-40" />
                                            <input placeholder="UM" value={s.unit || ""}
                                                onChange={e => { const ns = [...catEditing.specs_schema]; ns[i] = { ...ns[i], unit: e.target.value }; setCatEditing(v => ({ ...v, specs_schema: ns })); }}
                                                className="input-soft !py-1.5 text-xs col-span-1" />
                                            <button onClick={() => setCatEditing(v => ({ ...v, specs_schema: v.specs_schema.filter((_, j) => j !== i) }))}
                                                className="btn-ghost text-destructive col-span-1"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    ))}
                                    {(catEditing.specs_schema || []).length === 0 && (
                                        <p className="text-xs text-muted-foreground">Nicio specificație definită.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2">
                            <button onClick={() => setCatEditing(null)} className="btn-outline">Anulează</button>
                            <button onClick={saveCat} className="btn-primary">Salvează</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Product edit modal ── */}
            {prodOpen && prodEditing && (
                <ProductForm
                    value={prodEditing}
                    onChange={setProdEditing}
                    cats={allCats}
                    onCatsChange={setAllCats}
                    onSave={saveProd}
                    onClose={() => setProdOpen(false)} />
            )}

            {docsView && <DocsViewModal product={docsView} onClose={() => setDocsView(null)} />}
            {bulkOpen && <BulkPromoDialog onApply={applyPromoBulk} onClose={() => setBulkOpen(false)} />}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────
// ProductForm — full edit modal (unchanged logic)
// ─────────────────────────────────────────────────────────────────
function ProductForm({ value, onChange, cats, onCatsChange, onSave, onClose }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    const setSpec = (k, v) => onChange({ ...value, specs: { ...(value.specs || {}), [k]: v } });
    const category = cats.find(c => c.category_id === value.category_id);
    const [newSpecOptions, setNewSpecOptions] = React.useState({});
    const [importUrl, setImportUrl] = React.useState("");
    const [importing, setImporting] = React.useState(false);

    const handleImport = async () => {
        if (!importUrl.trim()) return;
        setImporting(true);
        try {
            const res = await api.post("/admin/scrape-product", { url: importUrl.trim(), download_images: true });
            const d = res.data;
            onChange({
                ...value,
                name:              d.name              || value.name,
                code:              d.code              || value.code,
                brand:             d.brand             || value.brand,
                slug:              d.slug              || value.slug,
                price:             d.price             || value.price,
                short_description: d.short_description || value.short_description,
                description:       d.description       || value.description,
                images:            d.images?.length ? d.images : value.images,
                thumbnail_index:   0,
                source_url:        d.source_url,
                specs:             { ...(value.specs || {}), ...(d.specs_raw || {}) },
            });
            toast.success("Produs importat! Verifică și modifică datele înainte de salvare.");
            setImportUrl("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare la import");
        }
        setImporting(false);
    };

    const prevCode = React.useRef(value.code || "");
    const handleCodeChange = (code) => {
        const wasAuto = !value.slug || value.slug === slugify(prevCode.current);
        prevCode.current = code;
        onChange({ ...value, code, slug: wasAuto ? slugify(code) : value.slug });
    };

    const addSpecOption = async (specKey, newOpt) => {
        if (!newOpt.trim() || !category) return;
        const updatedSchema = (category.specs_schema || []).map(s =>
            s.key === specKey ? { ...s, options: [...(s.options || []), newOpt.trim()] } : s
        );
        try {
            const r = await api.put(`/admin/categories/${category.category_id}`, { ...category, specs_schema: updatedSchema });
            onCatsChange(prev => prev.map(c => c.category_id === category.category_id ? r.data : c));
            setNewSpecOptions(prev => ({ ...prev, [specKey]: "" }));
            toast.success(`Opțiune "${newOpt.trim()}" adăugată`);
        } catch { toast.error("Eroare la salvarea opțiunii"); }
    };

    const dragIdx = React.useRef(null);
    const handleDragStart = (i) => { dragIdx.current = i; };
    const handleDrop = (i) => {
        if (dragIdx.current === null || dragIdx.current === i) return;
        const imgs = [...(value.images || [])];
        const [moved] = imgs.splice(dragIdx.current, 1);
        imgs.splice(i, 0, moved);
        let thumb = value.thumbnail_index ?? 0;
        if (thumb === dragIdx.current) thumb = i;
        else if (dragIdx.current < i && thumb > dragIdx.current && thumb <= i) thumb--;
        else if (dragIdx.current > i && thumb >= i && thumb < dragIdx.current) thumb++;
        onChange({ ...value, images: imgs, thumbnail_index: thumb });
        dragIdx.current = null;
    };

    const uploadImage = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        try {
            const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange({ ...value, images: [...(value.images || []), res.data.url] });
        } catch { toast.error("Eroare upload"); }
    };
    const removeImg = (idx) => {
        const imgs = value.images.filter((_, i) => i !== idx);
        let thumb = value.thumbnail_index ?? 0;
        if (thumb === idx) thumb = 0; else if (thumb > idx) thumb--;
        onChange({ ...value, images: imgs, thumbnail_index: thumb });
    };
    const uploadDoc = async (file, field) => {
        const fd = new FormData(); fd.append("file", file);
        try {
            const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            set(field, res.data.url); toast.success("Fișier încărcat");
        } catch { toast.error("Eroare upload"); }
    };

    const tops = cats.filter(c => !c.parent_id);
    const childrenOfCat = (id) => cats.filter(c => c.parent_id === id);

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
            <div className="bg-card rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-border">
                    <h2 className="font-heading text-xl">{value.product_id ? "Editează produs" : "Produs nou"}</h2>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                {/* Import din URL */}
                <div className="px-6 pt-5 pb-0">
                    <div className="bg-primary/5 border border-primary/20 rounded-xl p-3">
                        <p className="text-xs font-semibold text-primary mb-2 uppercase tracking-wide">Import automat din URL produs</p>
                        <div className="flex gap-2">
                            <input
                                value={importUrl}
                                onChange={e => setImportUrl(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && handleImport()}
                                placeholder="https://www.a2t.ro/..."
                                className="input-soft flex-1 text-sm !py-2"
                                disabled={importing}
                            />
                            <button
                                onClick={handleImport}
                                disabled={importing || !importUrl.trim()}
                                className="btn-primary text-sm !py-2 whitespace-nowrap disabled:opacity-50"
                            >
                                {importing ? "Se importă..." : "Importă"}
                            </button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1.5">Completează automat: nume, cod, brand, preț, descriere, imagini. Poți modifica orice după import.</p>
                    </div>
                </div>
                <div className="p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div><label className="text-xs uppercase text-muted-foreground">Cod</label><input value={value.code || ""} onChange={e => handleCodeChange(e.target.value)} className="input-soft" /></div>
                        <div><label className="text-xs uppercase text-muted-foreground">Brand</label><input value={value.brand || ""} onChange={e => set("brand", e.target.value)} className="input-soft" /></div>
                        <div className="md:col-span-2"><label className="text-xs uppercase text-muted-foreground">Nume</label><input value={value.name || ""} onChange={e => set("name", e.target.value)} className="input-soft" /></div>
                        <div className="md:col-span-2"><label className="text-xs uppercase text-muted-foreground">Slug</label><input value={value.slug || ""} onChange={e => set("slug", e.target.value)} className="input-soft" /></div>
                        <div><label className="text-xs uppercase text-muted-foreground">Categorie</label>
                            <select value={value.category_id || ""} onChange={e => set("category_id", e.target.value)} className="input-soft">
                                <option value="">— Selectează —</option>
                                {tops.map(parent => {
                                    const children = childrenOfCat(parent.category_id);
                                    if (children.length > 0) return (
                                        <optgroup key={parent.category_id} label={parent.name}>
                                            <option value={parent.category_id}>{parent.name} (general)</option>
                                            {children.map(c => <option key={c.category_id} value={c.category_id}>↳ {c.name}</option>)}
                                        </optgroup>
                                    );
                                    return <option key={parent.category_id} value={parent.category_id}>{parent.name}</option>;
                                })}
                            </select>
                        </div>
                        <div><label className="text-xs uppercase text-muted-foreground">Stoc fizic</label><input type="number" value={value.stock ?? 0} onChange={e => set("stock", Number(e.target.value))} className="input-soft" /></div>
                        <div><label className="text-xs uppercase text-muted-foreground">Preț</label><input type="number" step="0.01" value={value.price ?? 0} onChange={e => set("price", Number(e.target.value))} className="input-soft" /></div>
                        <div><label className="text-xs uppercase text-muted-foreground">Preț promoție</label><input type="number" step="0.01" value={value.sale_price ?? ""} onChange={e => set("sale_price", e.target.value === "" ? null : Number(e.target.value))} className="input-soft" /></div>
                        <div><label className="text-xs uppercase text-muted-foreground" title="Preț Recomandat Producător">PRP</label><input type="number" step="0.01" value={value.prp ?? ""} onChange={e => set("prp", e.target.value === "" ? null : Number(e.target.value))} className="input-soft" /></div>
                        <div><label className="text-xs uppercase text-muted-foreground">Garanție (luni)</label><input type="number" min="0" value={value.warranty_months ?? 24} onChange={e => set("warranty_months", Number(e.target.value))} className="input-soft" /></div>
                        <div><label className="text-xs uppercase text-muted-foreground">Stoc furnizor</label><input type="number" value={value.stock_supplier ?? 0} onChange={e => set("stock_supplier", Number(e.target.value))} className="input-soft" /></div>
                        <div><label className="text-xs uppercase text-muted-foreground">Livrare furnizor (zile)</label><input type="number" value={value.stock_supplier_eta_days ?? 0} onChange={e => set("stock_supplier_eta_days", Number(e.target.value))} className="input-soft" /></div>
                        <div><label className="text-xs uppercase text-muted-foreground">Prag stoc redus</label><input type="number" value={value.low_stock_threshold ?? 5} onChange={e => set("low_stock_threshold", Number(e.target.value))} className="input-soft" /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {[["manual_out_of_stock","Marchează «Stoc epuizat»"],["supplier_order_enabled","Acceptă comenzi furnizor"],["is_critical","Produs important"]].map(([k,l]) => (
                            <label key={k} className="flex items-center gap-2 card-soft p-3 cursor-pointer">
                                <input type="checkbox" checked={!!value[k]} onChange={e => set(k, e.target.checked)} />
                                <span className="text-sm">{l}</span>
                            </label>
                        ))}
                    </div>
                    <div><label className="text-xs uppercase text-muted-foreground">Descriere scurtă</label><textarea value={value.short_description || ""} onChange={e => set("short_description", e.target.value)} rows={2} className="input-soft" /></div>
                    <div>
                        <label className="text-xs uppercase text-muted-foreground block mb-1">Descriere completă</label>
                        <RichTextEditor value={value.description || ""} onChange={v => set("description", v)} />
                    </div>
                    <div>
                        <label className="text-xs uppercase text-muted-foreground block mb-1">Imagini</label>
                        <p className="text-xs text-muted-foreground mb-2">Trage pentru a reordona. <Star className="inline h-3 w-3" /> = imagine principală.</p>
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                            {(value.images || []).map((img, i) => (
                                <div key={i} draggable onDragStart={() => handleDragStart(i)} onDragOver={e => e.preventDefault()} onDrop={() => handleDrop(i)}
                                    className={`relative bg-muted rounded-lg overflow-hidden group cursor-grab border-2 transition-colors ${i === (value.thumbnail_index ?? 0) ? "border-primary" : "border-transparent"}`}>
                                    <img src={MEDIA(img)} alt="" className="w-full aspect-[4/3] object-contain" />
                                    <button type="button" onClick={() => set("thumbnail_index", i)}
                                        className={`absolute top-1 left-1 rounded-full p-1 transition-all ${i === (value.thumbnail_index ?? 0) ? "bg-primary text-primary-foreground" : "bg-black/50 text-white opacity-0 group-hover:opacity-100"}`}>
                                        <Star className="h-3 w-3" fill={i === (value.thumbnail_index ?? 0) ? "currentColor" : "none"} />
                                    </button>
                                    <button type="button" onClick={() => removeImg(i)} className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100">
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                            <label className="bg-muted rounded-lg border-2 border-dashed border-border hover:border-primary cursor-pointer flex flex-col items-center justify-center text-muted-foreground text-xs aspect-[4/3]">
                                <Upload className="h-5 w-5 mb-1" /><span>Adaugă</span>
                                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadImage(e.target.files[0])} />
                            </label>
                        </div>
                    </div>
                    {category?.specs_schema?.length > 0 && (
                        <div>
                            <label className="text-xs uppercase text-muted-foreground block mb-2">Specificații ({category.name})</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {category.specs_schema.map(sp => (
                                    <div key={sp.key}>
                                        <label className="text-xs text-muted-foreground block mb-1">{sp.label}{sp.unit && ` (${sp.unit})`}</label>
                                        {sp.type === "select" ? (
                                            <div className="space-y-1">
                                                <select value={value.specs?.[sp.key] || ""} onChange={e => setSpec(sp.key, e.target.value)} className="input-soft">
                                                    <option value="">—</option>
                                                    {sp.options.map(o => <option key={o}>{o}</option>)}
                                                </select>
                                                <div className="flex gap-1">
                                                    <input value={newSpecOptions[sp.key] || ""} onChange={e => setNewSpecOptions(p => ({ ...p, [sp.key]: e.target.value }))}
                                                        onKeyDown={e => e.key === "Enter" && addSpecOption(sp.key, newSpecOptions[sp.key] || "")}
                                                        placeholder="Adaugă opțiune..." className="input-soft !py-1 text-xs flex-1" />
                                                    <button type="button" onClick={() => addSpecOption(sp.key, newSpecOptions[sp.key] || "")} className="btn-outline !py-1 !px-2 text-xs"><Plus className="h-3 w-3" /></button>
                                                </div>
                                            </div>
                                        ) : sp.type === "boolean" ? (
                                            <label className="flex items-center gap-2 py-2"><input type="checkbox" checked={!!value.specs?.[sp.key]} onChange={e => setSpec(sp.key, e.target.checked)} /><span className="text-sm">Da</span></label>
                                        ) : sp.type === "number" ? (
                                            <input type="number" step="0.01" value={value.specs?.[sp.key] ?? ""} onChange={e => setSpec(sp.key, e.target.value === "" ? null : Number(e.target.value))} className="input-soft" />
                                        ) : (
                                            <input value={value.specs?.[sp.key] || ""} onChange={e => setSpec(sp.key, e.target.value)} className="input-soft" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Specificații importate (cheie liberă) */}
                    {Object.keys(value.specs || {}).some(k => !category?.specs_schema?.find(s => s.key === k)) && (
                        <details open className="card-soft p-4">
                            <summary className="cursor-pointer font-medium text-sm mb-3">Specificații tehnice importate</summary>
                            <div className="mt-3 divide-y divide-border">
                                {Object.entries(value.specs || {})
                                    .filter(([k]) => !category?.specs_schema?.find(s => s.key === k))
                                    .map(([k, v]) => (
                                        <div key={k} className="flex items-center gap-2 py-1.5 text-sm">
                                            <span className="text-muted-foreground w-1/2 shrink-0">{k}</span>
                                            <input
                                                value={v || ""}
                                                onChange={e => setSpec(k, e.target.value)}
                                                className="input-soft !py-1 text-xs flex-1"
                                            />
                                            <button type="button" onClick={() => {
                                                const s = { ...(value.specs || {}) };
                                                delete s[k];
                                                onChange({ ...value, specs: s });
                                            }} className="btn-ghost text-destructive !p-1 shrink-0">
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </details>
                    )}
                    <details className="card-soft p-4">
                        <summary className="cursor-pointer font-medium text-sm flex items-center gap-2"><span className="chip bg-primary/10 text-primary text-[10px]">Staff</span>Instalare & Documentație tehnică</summary>
                        <div className="mt-4 space-y-3">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs uppercase text-muted-foreground">Clipuri & Documente (staff only)</label>
                                    <button type="button" onClick={() => set("staff_docs", [...(value.staff_docs || []), { title: "", url: "", type: "video" }])} className="btn-outline !py-1 !px-2 text-xs flex items-center gap-1"><Plus className="h-3 w-3" /> Adaugă</button>
                                </div>
                                {(value.staff_docs || []).map((doc, i) => (
                                    <div key={i} className="card-soft p-2 grid grid-cols-12 gap-2 items-center mb-2">
                                        <select value={doc.type || "video"} onChange={e => set("staff_docs", value.staff_docs.map((d, j) => j === i ? { ...d, type: e.target.value } : d))} className="input-soft !py-1 text-xs col-span-2">
                                            <option value="video">Video</option><option value="manual">Manual</option><option value="document">Document</option>
                                        </select>
                                        <input value={doc.title || ""} onChange={e => set("staff_docs", value.staff_docs.map((d, j) => j === i ? { ...d, title: e.target.value } : d))} placeholder="Titlu" className="input-soft !py-1 text-xs col-span-3" />
                                        <input value={doc.url || ""} onChange={e => set("staff_docs", value.staff_docs.map((d, j) => j === i ? { ...d, url: e.target.value } : d))} placeholder="URL" className="input-soft !py-1 text-xs col-span-5" />
                                        <label className="btn-outline !py-1 !px-2 text-xs cursor-pointer col-span-1 flex justify-center">
                                            <Upload className="h-3 w-3" />
                                            <input type="file" className="hidden" onChange={async e => {
                                                if (!e.target.files[0]) return;
                                                const fd = new FormData(); fd.append("file", e.target.files[0]);
                                                try { const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("staff_docs", value.staff_docs.map((d, j) => j === i ? { ...d, url: r.data.url } : d)); } catch { toast.error("Eroare upload"); }
                                            }} />
                                        </label>
                                        <button type="button" onClick={() => set("staff_docs", value.staff_docs.filter((_, j) => j !== i))} className="btn-ghost text-destructive col-span-1"><X className="h-3 w-3" /></button>
                                    </div>
                                ))}
                            </div>
                            <div><label className="text-xs uppercase text-muted-foreground">Video instalare (YouTube URL)</label><input value={value.install_video_url || ""} onChange={e => set("install_video_url", e.target.value)} className="input-soft" /></div>
                            <div><label className="text-xs uppercase text-muted-foreground">Manual instalare</label>
                                <div className="flex gap-2">
                                    <input value={value.install_manual_url || ""} onChange={e => set("install_manual_url", e.target.value)} className="input-soft flex-1" />
                                    <label className="btn-outline cursor-pointer flex items-center gap-1 text-xs"><Upload className="h-3.5 w-3.5" /> PDF<input type="file" accept=".pdf" className="hidden" onChange={e => e.target.files[0] && uploadDoc(e.target.files[0], "install_manual_url")} /></label>
                                </div>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!value.install_public} onChange={e => set("install_public", e.target.checked)} /><span className="text-sm">Fă vizibil pentru clienți video + manual</span></label>
                        </div>
                    </details>
                    <details className="card-soft p-4">
                        <summary className="cursor-pointer font-medium text-sm">Documente produs (CE, manuale, fișe tehnice)</summary>
                        <div className="mt-4 space-y-3">
                            {[["doc_ce_url","Certificat CE"],["doc_manual_ro_url","Manual RO"],["doc_manual_en_url","Manual EN"],["doc_datasheet_url","Fișă tehnică"]].map(([field, label]) => (
                                <div key={field}><label className="text-xs uppercase text-muted-foreground">{label}</label>
                                    <div className="flex gap-2">
                                        <input value={value[field] || ""} onChange={e => set(field, e.target.value)} className="input-soft flex-1 text-sm" />
                                        <label className="btn-outline cursor-pointer flex items-center gap-1 text-xs"><Upload className="h-3.5 w-3.5" /> Upload<input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => e.target.files[0] && uploadDoc(e.target.files[0], field)} /></label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </details>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {[["active","Activ"],["featured","Popular"],["share_enabled","Share"],["google_merchant_enabled","Google"],["is_clearance","Lichidare"]].map(([k,l]) => (
                            <label key={k} className="flex items-center gap-2 card-soft p-3 cursor-pointer"><input type="checkbox" checked={!!value[k]} onChange={e => set(k, e.target.checked)} /><span className="text-sm">{l}</span></label>
                        ))}
                    </div>
                    <details className="card-soft p-4">
                        <summary className="cursor-pointer font-medium text-sm">SEO</summary>
                        <div className="mt-3 space-y-2">
                            <input placeholder="SEO Title" value={value.seo_title || ""} onChange={e => set("seo_title", e.target.value)} className="input-soft" />
                            <textarea placeholder="SEO Description" value={value.seo_description || ""} onChange={e => set("seo_description", e.target.value)} rows={2} className="input-soft" />
                            <input placeholder="SEO Keywords" value={value.seo_keywords || ""} onChange={e => set("seo_keywords", e.target.value)} className="input-soft" />
                        </div>
                    </details>
                </div>
                <div className="p-5 border-t border-border flex justify-end gap-2 bg-muted">
                    <button onClick={onClose} className="btn-outline">Anulează</button>
                    <button onClick={onSave} className="btn-primary" data-testid="product-save">Salvează</button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────
function DocsViewModal({ product: p, onClose }) {
    const docItems = [
        { field: "doc_ce_url", label: "Certificat CE" },
        { field: "doc_manual_ro_url", label: "Manual RO" },
        { field: "doc_manual_en_url", label: "Manual EN" },
        { field: "doc_datasheet_url", label: "Fișă tehnică" },
    ].filter(d => p[d.field]);
    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
            <div className="bg-card rounded-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg">Docs — {p.name}</h3>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="space-y-4">
                    {(p.staff_docs || []).length > 0 && <div>
                        <div className="text-xs uppercase text-muted-foreground mb-2">Staff docs</div>
                        {p.staff_docs.map((d, i) => (
                            <a key={i} href={d.url?.startsWith("http") ? d.url : MEDIA(d.url)} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-3 card-soft px-4 py-3 hover:border-primary hover:text-primary transition-colors text-sm mb-2">
                                {d.type === "video" ? <PlayCircle className="h-4 w-4 text-primary" /> : <FileText className="h-4 w-4 text-primary" />}
                                <span className="flex-1">{d.title || d.url}</span>
                                <Download className="h-3.5 w-3.5 text-muted-foreground" />
                            </a>
                        ))}
                    </div>}
                    {(p.install_video_url || p.install_manual_url) && <div>
                        <div className="text-xs uppercase text-muted-foreground mb-2">Instalare</div>
                        <div className="flex flex-wrap gap-2">
                            {p.install_video_url && <a href={p.install_video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 card-soft px-4 py-3 hover:border-primary hover:text-primary text-sm"><PlayCircle className="h-4 w-4 text-primary" /> Video instalare</a>}
                            {p.install_manual_url && <a href={MEDIA(p.install_manual_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 card-soft px-4 py-3 hover:border-primary hover:text-primary text-sm"><BookOpen className="h-4 w-4 text-primary" /> Manual <Download className="h-3.5 w-3.5 text-muted-foreground" /></a>}
                        </div>
                    </div>}
                    {docItems.length > 0 && <div>
                        <div className="text-xs uppercase text-muted-foreground mb-2">Documente produs</div>
                        <div className="flex flex-wrap gap-2">
                            {docItems.map(({ field, label }) => (
                                <a key={field} href={MEDIA(p[field])} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 card-soft px-4 py-3 hover:border-primary hover:text-primary text-sm">
                                    <FileText className="h-4 w-4" />{label}<Download className="h-3.5 w-3.5 text-muted-foreground" />
                                </a>
                            ))}
                        </div>
                    </div>}
                </div>
            </div>
        </div>
    );
}

function BulkPromoDialog({ onApply, onClose }) {
    const [pct, setPct] = React.useState("10");
    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
            <div className="bg-card rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                <h3 className="font-heading text-xl mb-4">Aplică reducere în masă</h3>
                <label className="text-xs uppercase text-muted-foreground">Procent reducere</label>
                <input type="number" value={pct} onChange={e => setPct(e.target.value)} className="input-soft mb-4" />
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="btn-outline">Anulează</button>
                    <button onClick={() => onApply(pct)} className="btn-primary">Aplică</button>
                </div>
            </div>
        </div>
    );
}
